import { parseISO } from 'date-fns';

import { FILE_TYPES } from 'common/src/file-storage/constants';
import { fileNameFromLocation } from 'common/src/file-storage/locationUtilities';
import { BASE, ID_PARAMS } from 'common/src/routes/BaseRoutes';
import { IBill } from 'common/src/types/interfaces';

import { getImportFileDownloadUrl } from 'portal/src/api/ImportFileService';
import {
  datePickerType,
  fileUploaderType,
  numberType,
  switchType,
  textInputType,
} from 'portal/src/components/common/forms/FormFieldTypes';
import { getStartAndEndDateFields } from 'portal/src/components/common/forms/commonFormFields';
import getInternalNotesField from 'portal/src/components/common/forms/internalNotesField';
import { getDateWithLastDay } from 'portal/src/utils/getDateWithLastDay';
import { getMonthWithMostDays } from 'portal/src/utils/getMonthWithMostDays';
import { validateEndDate } from 'portal/src/validators/dateValidator';

import { IBillHeaderFormValues } from './BillFormProvider';

export const defaultValues = (
  entity: Partial<IBill> = {},
): IBillHeaderFormValues => ({
  amount: entity.amount ?? 0,
  amountDue: entity.amountDue,
  balanceForward: entity.balanceForward,
  budgetPeriod: entity.budgetPeriod ? parseISO(entity.budgetPeriod) : null,
  date: entity.date ? parseISO(entity.date) : null,
  dueDate: entity.dueDate ? parseISO(entity.dueDate) : null,
  endDate: entity.endDate ? parseISO(entity.endDate) : null,
  estimated: entity.estimated ?? false,
  image: entity.statement?.imageLocation
    ? fileNameFromLocation(entity.statement.imageLocation)
    : (entity.image ?? ''),
  isPaid: entity.isPaid ?? false,
  lateFees: entity.lateFees,
  needsReview: entity.needsReview ?? false,
  internalNotes: entity.internalNotes ?? '',
  notes: entity.notes ?? '',
  number: entity.number ?? '',
  paidAmount: entity.paidAmount,
  paidDate: entity.paidDate ? parseISO(entity.paidDate) : null,
  rateSchedule: entity.rateSchedule ?? '',
  startDate: entity.startDate ? parseISO(entity.startDate) : null,
  workingImage: null,
});

export const getFields = ({
  setValue,
  getValues,
  id,
  showImageChangeMsg,
  billIsUsedInAllocations,
  imageWasSaved,
  billPageNumber = undefined,
  statementId = undefined,
  importFileId,
  hasSystemAdminAccess,
  isReadOnly,
}: {
  setValue: any;
  getValues: any;
  id: string | undefined;
  showImageChangeMsg: () => void;
  billIsUsedInAllocations: boolean;
  imageWasSaved: boolean;
  billPageNumber?: number;
  statementId?: string;
  importFileId?: string;
  hasSystemAdminAccess: boolean;
  isReadOnly: boolean;
}): (
  | datePickerType
  | fileUploaderType
  | numberType
  | switchType
  | textInputType
)[] => {
  const setDefaultBudgetPeriod = ({
    nextStartDate,
    nextEndDate,
  }: {
    nextStartDate: Date | null;
    nextEndDate: Date | null;
  }) => {
    const hasValidDates = validateEndDate(nextEndDate, nextStartDate);

    if (
      !nextStartDate ||
      !nextEndDate ||
      !hasValidDates ||
      getValues('budgetPeriod')
    )
      return;

    const { month, year } = getMonthWithMostDays(nextStartDate, nextEndDate);
    setValue('budgetPeriod', getDateWithLastDay(year, month), {
      shouldDirty: true,
    });
  };

  return [
    ...getStartAndEndDateFields({
      startLabel: 'Bill Start Date',
      startFieldDisabled: billIsUsedInAllocations,
      endLabel: 'Bill End Date',
      endFieldDisabled: billIsUsedInAllocations,
      required: true,
      onStartChange: (nextStartDate: Date | null) => {
        setDefaultBudgetPeriod({
          nextStartDate,
          nextEndDate: getValues('endDate'),
        });
      },
      onEndChange: (nextEndDate: Date | null) => {
        setDefaultBudgetPeriod({
          nextStartDate: getValues('startDate'),
          nextEndDate,
        });
      },
    }),
    {
      disabled: billIsUsedInAllocations,
      placeholder: 'Bill Amount',
      label: 'Bill Amount',
      name: 'amount',
      type: 'number',
      required: true,
      adorment: '$ ',
      allowNegative: true,
      autoDecimalFormat: true,
    },
    {
      placeholder: 'Bill Date',
      label: 'Bill Date',
      name: 'date',
      type: 'datePicker',
      required: false,
    },
    {
      placeholder: 'Due date',
      label: 'Due date',
      name: 'dueDate',
      type: 'datePicker',
      required: false,
    },
    {
      placeholder: 'Budget period',
      label: 'Budget period',
      name: 'budgetPeriod',
      type: 'datePicker',
      required: false,
      other: {
        views: ['year', 'month'],
      },
    },
    {
      placeholder: 'Bill Number',
      label: 'Bill Number',
      name: 'number',
      type: 'textInput',
      required: false,
    },
    {
      label: 'Is this an estimated bill?',
      name: 'estimated',
      type: 'switch',
      required: false,
    },
    {
      placeholder: 'Notes',
      label: 'Notes',
      multiline: true,
      name: 'notes',
      type: 'textInput',
      required: false,
    },
    getInternalNotesField(hasSystemAdminAccess),
    {
      label: 'Bill Image',
      name: 'workingImage',
      chooseDisabled: isReadOnly,
      deleteDisabled: isReadOnly,
      fileName: getValues('image'),
      type: 'fileUploader',
      downloadPath: importFileId
        ? getImportFileDownloadUrl(importFileId)
        : undefined,
      required: false,
      accept: `.${FILE_TYPES.billUpload.join(', .')}`,
      ...(imageWasSaved
        ? {
            viewPath: `${
              statementId
                ? BASE.IMAGES.STATEMENT.replace(ID_PARAMS.ANY, statementId)
                : BASE.IMAGES.BILL.replace(ID_PARAMS.ANY, id)
            }${billPageNumber ? `?page=${billPageNumber}` : ''}`,
          }
        : {}),
      onChange: (file: File) => {
        setValue('workingImage', file);
        setValue('image', file.name, {
          shouldDirty: true,
        });
        showImageChangeMsg();
      },
      onDelete: () => {
        setValue('workingImage', null);
        setValue('image', '', { shouldDirty: true });
        showImageChangeMsg();
      },
      tooltip: `View bill image${
        billPageNumber ? `; bill starts on page ${billPageNumber}` : ''
      }`,
    },
    {
      placeholder: 'Amount Due',
      label: 'Amount Due',
      name: 'amountDue',
      type: 'number',
      required: false,
      adorment: '$ ',
      allowNegative: true,
      autoDecimalFormat: true,
      prependDivider: 'Payment Information',
    },
    {
      placeholder: 'Balance Forward',
      label: 'Balance Forward',
      name: 'balanceForward',
      type: 'number',
      required: false,
      adorment: '$ ',
      allowNegative: true,
      autoDecimalFormat: true,
    },
    {
      placeholder: 'Late Fees',
      label: 'Late Fees',
      name: 'lateFees',
      type: 'number',
      required: false,
      adorment: '$ ',
      allowNegative: true,
      autoDecimalFormat: true,
    },
    {
      label: 'Is this bill paid?',
      name: 'isPaid',
      type: 'switch',
      required: false,
    },
    {
      placeholder: 'Paid amount',
      label: 'Paid amount',
      name: 'paidAmount',
      type: 'number',
      required: false,
      adorment: '$ ',
      autoDecimalFormat: true,
    },
    {
      placeholder: 'Paid date',
      label: 'Paid date',
      name: 'paidDate',
      type: 'datePicker',
      required: false,
    },
    {
      placeholder: 'Rate Schedule',
      label: 'Rate Schedule',
      name: 'rateSchedule',
      type: 'textInput',
      required: false,
    },
  ];
};
